import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  IconButton,
  LinearProgress,
  TextField,
  TablePagination,
  Typography
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import CloseIcon from "@material-ui/icons/Close";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import SearchIcon from "@material-ui/icons/Search";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import DataSettingsToggle from "features/data-settings/DataSettingsToggle";
import SettingsCard from "components/kea/SettingsCard/SettingsCard";
import tableStyles from "components/kea/tables/DataTable.module.scss";
import SegmentedControl from "components/kea/SegmentedControl";
import {
  useUnsavedChangesGuard,
  UnsavedChangesDialog
} from "components/kea/UnsavedChangesGuard";
import { useLocalizedMessage } from "util/useLocalizedMessage";
import { createDescriptionParametersApi } from "./bridgeDescriptionParametersApi";
import DescriptionParameterEditor from "./DescriptionParameterEditor";
import DescriptionParameterCandidateRow from "./DescriptionParameterCandidateRow";
import DescriptionParametersConfirmDialog from "./DescriptionParametersConfirmDialog";
import styles from "./DescriptionParametersSection.module.scss";
import inputClasses from "./descriptionParameterInputClasses";
import { normalizationSettings } from "./descriptionNormalization";
const defaults = {
  mode: "auto",
  separator: ":",
  source_fields: ["description"]
};
const busyJob = state =>
  state && ["queued", "running"].includes(state.job.status);
const metadata = decision => ({
  role: decision.role,
  title: decision.title,
  searchable: decision.searchable,
  filterable: decision.filterable,
  display: decision.display
});
const sameMetadata = (left, right) =>
  !!left &&
  !!right &&
  JSON.stringify(metadata(left)) === JSON.stringify(metadata(right));
const sameIdentity = (left, right) =>
  !!left &&
  !!right &&
  left.role === right.role &&
  left.target_key === right.target_key &&
  (left.value_separator || null) === (right.value_separator || null) &&
  JSON.stringify(normalizationSettings(left)) ===
    JSON.stringify(normalizationSettings(right)) &&
  JSON.stringify(left.value_aliases || {}) ===
    JSON.stringify(right.value_aliases || {});
const ownDecision = (item, role) => ({
  role,
  target_key: `field:${item.field_id}:${role}`,
  title: item.label,
  searchable: role !== "ignore",
  filterable: role === "parameter",
  display: role === "parameter",
  value_aliases: {},
  value_separator: null
});
const foldSearchText = value =>
  String(value == null ? "" : value)
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[łŁ]/g, "l")
    .toLocaleLowerCase();

export default function DescriptionParametersSection({
  enabled,
  standalone = false,
  renderPage,
  normalizationEnabled = false,
  lifecycleEnabled = false,
  bridgeClient,
  scopeKey,
  pollMs = 2000,
  saveBatchSize = 100,
  onCatalogChange,
  catalogUpdating = false,
  catalogRefreshKey
}) {
  const t = useLocalizedMessage();
  const tr = (key, values) => t(`descriptionParameters.${key}`, values);
  const api = useMemo(() => createDescriptionParametersApi(bridgeClient), [
    bridgeClient
  ]);
  const [state, setState] = useState(null);
  const [config, setConfig] = useState(defaults);
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState({});
  const [draft, setDraft] = useState({});
  const [groupDraft, setGroupDraft] = useState({});
  const [cursor, setCursor] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const catalogViewport = useRef(null);
  const notifiedAnalysis = useRef(null);
  const [catalogReady, setCatalogReady] = useState(false);
  const [catalogRefreshing, setCatalogRefreshing] = useState(false);
  const [catalogConflict, setCatalogConflict] = useState(false);
  const [error, setError] = useState(null);
  const [working, setWorking] = useState(false);
  const [activeScope, setActiveScope] = useState(scopeKey);
  const [confirm, setConfirm] = useState(null);
  const [catalogFilter, setCatalogFilter] = useState("all");
  const [catalogQuery, setCatalogQuery] = useState("");
  const [catalogSort, setCatalogSort] = useState({
    key: "products",
    direction: "desc"
  });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [editorItem, setEditorItem] = useState(null);
  const epoch = useRef(0);
  const independentTargets = useRef({});
  const scopeRef = useRef(scopeKey);
  const mounted = useRef(true);
  const reviewRef = useRef(null);
  reviewRef.current = { state, draft, groupDraft };
  if (scopeRef.current !== scopeKey) {
    scopeRef.current = scopeKey;
    epoch.current++;
  }
  const changedConfig =
    state && JSON.stringify(config) !== JSON.stringify(state.config);
  const reviewDirty =
    Object.keys(draft).length > 0 || Object.keys(groupDraft).length > 0;
  const dirty = reviewDirty || !!changedConfig;
  const guard = useUnsavedChangesGuard(dirty);
  const scopeChanged = activeScope !== scopeKey;
  const disabled = working || catalogRefreshing || busyJob(state) || scopeChanged || catalogUpdating;
  const policy = state?.policy || {
    enabled: false,
    refresh_before_rebuild: false
  };
  const sourceDisabled = disabled || !policy.enabled;
  const executionBlocked =
    busyJob(state) && state.execution?.status === "blocked";
  const effective = useCallback(item => {
    const decision = draft[item.field_id] || item.decision;
    return decision &&
      decision.role !== "ignore" &&
      groupDraft[decision.target_key]
      ? { ...decision, ...groupDraft[decision.target_key] }
      : decision;
  }, [draft, groupDraft]);
  const allGroups = Object.assign(Object.create(null), groups, groupDraft);
  items.forEach(item => {
    const d = effective(item);
    if (d && d.role !== "ignore")
      allGroups[d.target_key] = { ...allGroups[d.target_key], ...metadata(d) };
  });
  const independentTarget = item => {
    const decision = effective(item);
    const base = `field:${item.field_id}:${decision.role}`;
    const currentTarget = decision.target_key;
    const currentVisibleMembers = items.filter(row => {
      const value = effective(row);
      return (
        value && value.role !== "ignore" && value.target_key === currentTarget
      );
    }).length;
    if (
      Math.max(
        allGroups[currentTarget]?.member_count || 0,
        currentVisibleMembers
      ) <= 1
    )
      return currentTarget;
    const candidate = decision.target_key.startsWith(base)
      ? decision.target_key
      : base;
    const visibleMembers = items.filter(row => {
      const value = effective(row);
      return value && value.role !== "ignore" && value.target_key === candidate;
    }).length;
    if (Math.max(allGroups[candidate]?.member_count || 0, visibleMembers) <= 1)
      return candidate;
    const cacheKey = `${item.field_id}:${candidate}`;
    if (!independentTargets.current[cacheKey]) {
      independentTargets.current[cacheKey] = `${base}:split:${Date.now()}:${
        Object.keys(independentTargets.current).length
      }`;
    }
    return independentTargets.current[cacheKey];
  };
  const live = token => mounted.current && token === epoch.current;
  const accept = value => {
    setState(value);
  };
  const readCatalog = async (status, next = null, token = epoch.current, options = {}) => {
    const current = () => live(token) && (!options.current || options.current());
    const validate = data => {
      if (
        data.revision !== status.revision ||
        JSON.stringify(data.scope) !== JSON.stringify(status.scope)
      )
        throw Object.assign(new Error(), { code: "REVISION_CONFLICT" });
    };
    const data = await api("catalog", { cursor: next, limit: 100 });
    if (!current()) return;
    validate(data);
    const refreshed = data.items.slice();
    let nextCursor = data.next_cursor;
    // Search, sort and paging share one revision-consistent bounded catalog.
    // The backend caps approved fields at 1000; products are never loaded here.
    const seenCursors = new Set();
    for (let page = 1; nextCursor && page < 10; ++page) {
      if (seenCursors.has(nextCursor))
        throw Object.assign(new Error(), { code: "REVISION_CONFLICT" });
      seenCursors.add(nextCursor);
      const more = await api("catalog", { cursor: nextCursor, limit: 100 });
      if (!current()) return;
      validate(more);
      refreshed.push(...more.items);
      nextCursor = more.next_cursor;
    }
    if (options.validateReview) {
      const found = new Set(refreshed.map(item => item.field_id));
      const missingFields = () => Object.keys(reviewRef.current.draft).filter(id => !found.has(id));
      const review = reviewRef.current;
      const missingGroups = Object.keys(review.groupDraft).some(key =>
        !data.groups?.[key] && !Object.entries(review.draft).some(([id, decision]) =>
          found.has(id) && decision.target_key === key && decision.role !== "ignore"
        )
      );
      const conflict = missingFields().length > 0 || missingGroups;
      setCatalogConflict(conflict);
      if (conflict) setError("REVISION_CONFLICT");
      else if (catalogConflict) setError(previous => previous === "REVISION_CONFLICT" ? null : previous);
    }
    setItems(refreshed.slice(0, 1000));
    setGroups(data.groups || {});
    setCursor(nextCursor);
    setCatalogReady(true);
  };
  const reload = async () => {
    const token = ++epoch.current;
    setWorking(true);
    setError(null);
    setCatalogReady(false);
    setCatalogRefreshing(false);
    setCatalogConflict(false);
    try {
      const value = await api("status");
      if (!live(token)) return;
      accept(value);
      setConfig(value.config);
      setDraft({});
      setGroupDraft({});
      setItems([]);
      setCursor(null);
      if (!busyJob(value)) await readCatalog(value, null, token);
    } catch (failure) {
      if (live(token)) setError(failure.code || "REQUEST_FAILED");
    } finally {
      if (live(token)) setWorking(false);
    }
  };
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      epoch.current++;
    };
  }, []);
  useEffect(() => {
    if (!enabled) return undefined;
    if (scopeChanged && dirty) return undefined;
    setActiveScope(scopeKey);
    reload();
    return () => {
      epoch.current++;
    };
    // Scope transitions deliberately preserve unsaved edits until explicit discard.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, api, scopeKey]);
  useEffect(() => {
    if (!enabled || !busyJob(state) || working || scopeChanged || error)
      return undefined;
    const token = epoch.current;
    const timer = setTimeout(async () => {
      try {
        const value = await api("status");
        if (!live(token)) return;
        accept(value);
        if (!busyJob(value)) await readCatalog(value, null, token);
      } catch (failure) {
        if (live(token)) setError(failure.code || "REQUEST_FAILED");
      }
    }, pollMs);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, enabled, api, working, scopeChanged, error, pollMs]);
  useEffect(() => {
    if (!enabled || catalogRefreshKey == null || scopeChanged) return undefined;
    const token = epoch.current;
    let current = true;
    api("status")
      .then(async value => {
        if (
          !current ||
          !live(token) ||
          (reviewRef.current.state &&
            value.revision < reviewRef.current.state.revision)
        ) return;
        setCatalogRefreshing(true);
        setCursor(null);
        accept(value);
        if (!busyJob(value)) {
          setCatalogConflict(true);
          await readCatalog(value, null, token, {
            validateReview: true,
            current: () => current
          });
        }
      })
      .catch(failure => {
        if (current && live(token)) setError(failure.code || "REQUEST_FAILED");
      })
      .finally(() => {
        if (current && live(token)) setCatalogRefreshing(false);
      });
    return () => {
      current = false;
    };
    // Refresh the published analysis and its cursors while retaining review and parser drafts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, enabled, scopeKey, catalogRefreshKey]);
  const catalogChanged = async () => {
    if (onCatalogChange) await onCatalogChange();
  };
  useEffect(() => {
    if (!enabled || !policy.enabled || !catalogReady || scopeChanged
      || state?.job.kind !== "analysis" || state?.job.status !== "completed"
      || state?.application.state !== "not_applied") return;
    const identity = `${scopeKey}:${state.job.id}`;
    if (notifiedAnalysis.current === identity) return;
    notifiedAnalysis.current = identity;
    Promise.resolve(onCatalogChange?.()).catch(() => {
      if (mounted.current) setError("REQUEST_FAILED");
    });
  }, [enabled, policy.enabled, catalogReady, scopeChanged, scopeKey, state, onCatalogChange]);
  const run = async work => {
    const token = epoch.current;
    setWorking(true);
    setError(catalogConflict ? "REVISION_CONFLICT" : null);
    try {
      await work(token);
    } catch (failure) {
      if (live(token)) setError(failure.code || "REQUEST_FAILED");
    } finally {
      if (live(token)) setWorking(false);
    }
  };
  const updatePolicy = patch => {
    if (!enabled || !lifecycleEnabled || !state || disabled) return;
    // An earlier publication refresh must not replace the confirmed setting.
    epoch.current++;
    run(async token => {
      const value = await api("settings", {
        expected_revision: state.revision,
        policy: { ...policy, ...patch }
      });
      if (!live(token)) return;
      accept(value);
      if (Object.prototype.hasOwnProperty.call(patch, "enabled")) await catalogChanged();
      if (!live(token)) return;
      setCursor(null);
      if (!busyJob(value)) await readCatalog(value, null, token);
    });
  };
  const save = async token => {
    let current = state;
    // Group metadata can touch absent members; each request is atomic and revision fenced.
    const groupEntries = Object.entries(groupDraft).filter(
      ([key]) => groups[key]
    );
    for (let i = 0; i < groupEntries.length; i += saveBatchSize) {
      current = await api("save", {
        expected_revision: current.revision,
        decisions: {},
        groups: Object.fromEntries(groupEntries.slice(i, i + saveBatchSize))
      });
      if (!live(token)) return;
      accept(current);
      await catalogChanged();
      if (!live(token)) return;
      const acceptedGroups = Object.fromEntries(
        groupEntries.slice(i, i + saveBatchSize)
      );
      setGroups(previous => ({
        ...previous,
        ...Object.fromEntries(
          Object.entries(acceptedGroups).map(([key, value]) => [
            key,
            { ...previous[key], ...value }
          ])
        )
      }));
      setItems(previous =>
        previous.map(item =>
          item.decision &&
          item.decision.role !== "ignore" &&
          acceptedGroups[item.decision.target_key]
            ? {
                ...item,
                decision: {
                  ...item.decision,
                  ...acceptedGroups[item.decision.target_key]
                }
              }
            : item
        )
      );
      setGroupDraft(previous => {
        const next = { ...previous };
        groupEntries
          .slice(i, i + saveBatchSize)
          .forEach(([key]) => delete next[key]);
        return next;
      });
    }
    const entries = Object.entries(draft).map(([id, decision]) => [
      id,
      decision.role !== "ignore" && groupDraft[decision.target_key]
        ? { ...decision, ...groupDraft[decision.target_key] }
        : decision
    ]);
    for (let i = 0; i < entries.length; i += saveBatchSize) {
      const chunk = Object.fromEntries(entries.slice(i, i + saveBatchSize));
      current = await api("save", {
        expected_revision: current.revision,
        decisions: chunk
      });
      if (!live(token)) return;
      accept(current);
      await catalogChanged();
      if (!live(token)) return;
      setItems(previous =>
        previous.map(item =>
          chunk[item.field_id]
            ? { ...item, decision: chunk[item.field_id] }
            : item
        )
      );
      setDraft(previous => {
        const next = { ...previous };
        Object.keys(chunk).forEach(key => delete next[key]);
        return next;
      });
    }
    setGroupDraft({});
    if (!busyJob(current)) await readCatalog(current, null, token);
  };
  const analyze = () =>
    run(async token => {
      const value = await api("start", {
        expected_revision: state.revision,
        config
      });
      if (!live(token)) return;
      accept(value);
      setConfig(value.config);
      setDraft({});
      setGroupDraft({});
      setCatalogConflict(false);
      setError(null);
      setItems([]);
      setCatalogReady(false);
      setCursor(null);
    });
  const cancel = () => {
    // A running status request must not overwrite the cancellation response.
    epoch.current++;
    run(async token => {
      const value = await api("cancel", {
        expected_revision: state.revision,
        job_id: state.job.id
      });
      if (!live(token)) return;
      accept(value);
      if (!busyJob(value)) await readCatalog(value, null, token);
    });
  };
  const requestReload = () => (dirty ? setConfirm("reload") : reload());
  const createRoleDecision = useCallback((item, role, current = effective(item)) => {
    if (!role) return;
    const old = current;
    const own = ownDecision(item, role);
    if (old && old.role !== role && role !== "ignore")
      own.target_key = `${own.target_key}:split:${Date.now()}:${epoch.current}`;
    return {
      ...own,
      title: old ? old.title : own.title,
      value_aliases: old ? old.value_aliases : {},
      value_separator: old ? old.value_separator || null : null,
      ...(old?.value_normalization
        ? { value_normalization: old.value_normalization }
        : {})
    };
  }, [effective]);
  const commitDecision = useCallback((item, decision, groupMetadata = null) => {
    const current = effective(item);
    const metadataChanged = !sameMetadata(current, decision);
    const identityChanged = !sameIdentity(current, decision);
    if (metadataChanged || identityChanged) {
      setDraft(previous => ({ ...previous, [item.field_id]: decision }));
    }
    if (
      groupMetadata &&
      metadataChanged &&
      current?.target_key === decision.target_key
    ) {
      setGroupDraft(previous => ({
        ...previous,
        [decision.target_key]: groupMetadata
      }));
    }
  }, [effective]);
  const classify = useCallback((item, role) => {
    if (!role) return;
    const decision = createRoleDecision(item, role);
    commitDecision(
      item,
      decision,
      role === "ignore" ? null : metadata(decision)
    );
  }, [createRoleDecision, commitDecision]);
  const roleState = item => {
    const decision = effective(item);
    if (!decision) return "pending";
    if (decision.role === "ignore") return "ignored";
    return decision.role === "identifier" ? "searchable" : "filter";
  };
  const setSort = key =>
    setCatalogSort(current => ({
      key,
      direction:
        current.key === key
          ? current.direction === "asc"
            ? "desc"
            : "asc"
          : key === "name"
          ? "asc"
          : "desc"
    }));
  const sortValue = item =>
    catalogSort.key === "products"
      ? item.product_count
      : catalogSort.key === "values"
      ? item.distinct_value_count
      : item.label;
  const visibleItems = items
    .filter(item => {
      const stateName = roleState(item);
      if (catalogFilter !== "all" && catalogFilter !== stateName) return false;
      const needle = foldSearchText(catalogQuery.trim());
      if (!needle) return true;
      return [
        item.label,
        effective(item)?.title,
        ...(item.examples || [])
      ].some(value => foldSearchText(value).includes(needle));
    })
    .slice()
    .sort((a, b) => {
      const left = sortValue(a);
      const right = sortValue(b);
      const compared =
        typeof left === "number"
          ? left - right
          : String(left).localeCompare(String(right), undefined, {
              numeric: true,
              sensitivity: "base"
            });
      const directed = catalogSort.direction === "asc" ? compared : -compared;
      return directed || a.label.localeCompare(b.label);
    });
  const currentPage = Math.min(page, Math.max(0, Math.ceil(visibleItems.length / pageSize) - 1));
  const pageItems = visibleItems.slice(currentPage * pageSize, (currentPage + 1) * pageSize);
  useEffect(() => { setPage(0); }, [catalogFilter, catalogQuery, catalogSort, pageSize]);
  useEffect(() => {
    if (catalogViewport.current) catalogViewport.current.scrollTop = 0;
  }, [currentPage, catalogFilter, catalogQuery, catalogSort, pageSize]);
  const undoChanges = () => {
    setDraft({});
    setGroupDraft({});
    setConfig(state.config);
    if (catalogConflict) {
      setCatalogConflict(false);
      setError(previous => previous === "REVISION_CONFLICT" ? null : previous);
    }
  };
  const editorDecision = useMemo(
    () => (editorItem ? effective(editorItem) : null),
    // Group metadata is combined by effective(); preserve reference during status polling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editorItem, draft, groupDraft]
  );
  if (!enabled) return null;
  const pageActions = state && (
    <div className={styles.pageActions}>
      {dirty && <Button variant="outlined" disabled={disabled} onClick={undoChanges}>{tr("undoChanges")}</Button>}
      <Button variant="contained" color="primary"
        disabled={sourceDisabled || catalogConflict || !reviewDirty || !!changedConfig}
        onClick={() => run(token => save(token))}>{tr("save")}</Button>
    </div>
  );
  const content = (
    <SettingsCard className={`${styles.card} ${standalone ? styles.standaloneCard : ""}`} data-testid="description-parameters">
      <div className={styles.header}>
        <div className={styles.heading}>
          {!standalone && (
            <>
              <h2 className={styles.title}>{tr("productTitle")}</h2>
              <p className={styles.intro}>{tr("productIntro")}</p>
            </>
          )}
          {state && lifecycleEnabled && (
            <label className={styles.sourceToggle}>
              <DataSettingsToggle
                checked={policy.enabled}
                disabled={disabled}
                label={tr("sourceEnabled")}
                inputProps={{ role: "switch" }}
                onChange={event => updatePolicy({ enabled: event.target.checked })}
              />
              <span className={styles.sourceToggleLabel}>{tr("sourceEnabled")}</span>
            </label>
          )}
        </div>
        {state && (
          <div className={styles.headerActions}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<SettingsOutlinedIcon />}
              onClick={() => setSettingsOpen(true)}
              disabled={disabled}
              aria-label={tr("analysisSettings")}
            >
              {tr("settings")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={
                sourceDisabled ||
                !config.source_fields.length ||
                !config.separator
              }
              onClick={() => (reviewDirty ? setConfirm("analyze") : analyze())}
            >
              {tr("analyze")}
            </Button>
            {!renderPage && pageActions}
          </div>
        )}
      </div>
      {changedConfig && <p className={styles.footerState} role="status">{tr("analyzeFirst")}</p>}
      {scopeChanged && (
        <Alert severity="warning" className={styles.notice}>
          {tr("scopeChanged")}
          <Button
            onClick={() => {
              setActiveScope(scopeKey);
              reload();
            }}
          >
            {tr("discard")}
          </Button>
        </Alert>
      )}
      {error && (
        <Alert severity="error" className={styles.notice}>
          <Typography>{tr(`error.${error}`)}</Typography>
          <Button onClick={requestReload}>{tr("reload")}</Button>
        </Alert>
      )}
      {executionBlocked && (
        <Alert
          severity="warning"
          className={styles.notice}
          action={
            state.execution.retryable === true ? (
              <Button
                color="inherit"
                disabled={working || scopeChanged || catalogUpdating}
                onClick={() =>
                  run(async token => {
                    const value = await api("retry", {
                      expected_revision: state.revision,
                      job_id: state.job.id
                    });
                    if (!live(token)) return;
                    accept(value);
                    if (!busyJob(value)) await readCatalog(value, null, token);
                  })
                }
              >
                {tr("retryBackgroundTask")}
              </Button>
            ) : (
              undefined
            )
          }
        >
          {tr("backgroundBlockedHelp")}
        </Alert>
      )}
      {!state && !error && (
        <Typography role="status">{tr("loading")}</Typography>
      )}
      {state && (
        <>
          {!catalogUpdating && (busyJob(state) ||
            ["failed", "cancelled"].includes(state.job.status)) && (
            <div
              className={styles.jobPanel}
              role="status"
              aria-live="polite"
              aria-atomic="true"
            >
              <div className={styles.jobHeader}>
                <div className={styles.jobCopy}>
                  <span className={styles.jobTitle}>
                    {executionBlocked || state.job.status === "queued"
                      ? tr("backgroundWaiting")
                      : busyJob(state)
                      ? tr(
                          state.job.kind === "materialization"
                            ? "preparing"
                            : "analyzing"
                        )
                      : tr(`status.${state.job.status}`)}
                  </span>
                  {busyJob(state) && (
                    <span className={styles.jobDetail}>
                      {tr("progress", state.job)}
                    </span>
                  )}
                </div>
                {busyJob(state) ? (
                  <Button
                    size="small"
                    disabled={working || scopeChanged || catalogUpdating}
                    onClick={cancel}
                  >
                    {tr("cancelJob")}
                  </Button>
                ) : (
                  <Button
                    size="small"
                    disabled={working}
                    onClick={requestReload}
                  >
                    {tr("reload")}
                  </Button>
                )}
              </div>
              {busyJob(state) && (
                <LinearProgress
                  variant={state.job.total ? "determinate" : "indeterminate"}
                  value={
                    state.job.total
                      ? (100 * state.job.processed) / state.job.total
                      : undefined
                  }
                />
              )}
            </div>
          )}

          {catalogReady && !busyJob(state) && (
            <>
              {!!items.length ? (
                <div className={styles.library}>
                  <div className={styles.toolbar}>
                    <label className={styles.search}>
                      <SearchIcon />
                      <input
                        type="search"
                        value={catalogQuery}
                        onChange={event => setCatalogQuery(event.target.value)}
                        placeholder={tr("searchCandidates")}
                        aria-label={tr("searchCandidates")}
                      />
                    </label>
                    <SegmentedControl
                      className={styles.segments}
                      value={catalogFilter}
                      label={tr("candidateFilter")}
                      onChange={setCatalogFilter}
                      options={[
                        {
                          value: "all",
                          label: tr("all"),
                          ariaLabel: tr("allFields")
                        },
                        {
                          value: "pending",
                          label: tr("needsReview"),
                          ariaLabel: tr("needsReview")
                        },
                        {
                          value: "filter",
                          label: tr("filters"),
                          ariaLabel: tr("filters")
                        },
                        {
                          value: "searchable",
                          label: tr("searchable"),
                          ariaLabel: tr("searchable")
                        },
                        {
                          value: "ignored",
                          label: tr("ignored"),
                          ariaLabel: tr("ignored")
                        }
                      ]}
                    />
                    <label className={styles.mobileSort}>
                      <span>{tr("sortBy")}</span>
                      <select
                        aria-label={tr("sortBy")}
                        value={`${catalogSort.key}:${catalogSort.direction}`}
                        onChange={event => {
                          const [key, direction] = event.target.value.split(
                            ":"
                          );
                          setCatalogSort({ key, direction });
                        }}
                      >
                        <option value="products:desc">
                          {tr("sortMostProducts")}
                        </option>
                        <option value="values:desc">
                          {tr("sortMostValues")}
                        </option>
                        <option value="name:asc">{tr("sortName")}</option>
                      </select>
                    </label>
                  </div>
                  <div className={styles.catalogViewport} ref={catalogViewport} data-testid="description-catalog-viewport">
                  <div
                    className={styles.candidateHeader}
                    data-testid="description-candidate-header"
                    data-sticky="true"
                    role="row"
                  >
                    <button
                      type="button"
                      aria-label={tr("sortParameter")}
                      onClick={() => setSort("name")}
                    >
                      {tr("parameterColumn")}
                      {catalogSort.key === "name" &&
                        (catalogSort.direction === "asc" ? (
                          <ArrowUpwardIcon />
                        ) : (
                          <ArrowDownwardIcon />
                        ))}
                    </button>
                    <span>{tr("examples")}</span>
                    <button
                      type="button"
                      aria-label={tr("sortProducts")}
                      onClick={() => setSort("products")}
                    >
                      {tr("products")}
                      {catalogSort.key === "products" &&
                        (catalogSort.direction === "asc" ? (
                          <ArrowUpwardIcon />
                        ) : (
                          <ArrowDownwardIcon />
                        ))}
                    </button>
                    <button
                      type="button"
                      aria-label={tr("sortValues")}
                      onClick={() => setSort("values")}
                    >
                      {tr("observedValues")}
                      {catalogSort.key === "values" &&
                        (catalogSort.direction === "asc" ? (
                          <ArrowUpwardIcon />
                        ) : (
                          <ArrowDownwardIcon />
                        ))}
                    </button>
                    <span>{tr("useAs")}</span>
                    <span aria-hidden="true" />
                  </div>
                  <div className={styles.candidateList}>
                    {pageItems.map(item => {
                      const decision = effective(item);
                      return <DescriptionParameterCandidateRow
                        key={item.field_id}
                        item={item}
                        title={decision?.title || item.label}
                        role={decision?.role || ""}
                        disabled={sourceDisabled}
                        onReview={setEditorItem}
                        onRoleChange={classify}
                      />;
                    })}
                    {!visibleItems.length && (
                      <div className={styles.empty}>
                        {tr("noMatchingFields")}
                      </div>
                    )}
                  </div>
                  </div>
                  {cursor && <Alert severity="info">{tr("ceiling")}</Alert>}
                  <TablePagination
                    className={`${tableStyles.pagination} ${styles.pagination}`}
                    classes={{ toolbar: styles.paginationToolbar, selectRoot: styles.pageSizeSelect }}
                    component="div"
                    rowsPerPageOptions={[25, 50, 100]}
                    count={visibleItems.length}
                    rowsPerPage={pageSize}
                    page={currentPage}
                    onPageChange={(event, nextPage) => setPage(nextPage)}
                    onRowsPerPageChange={event => { setPageSize(Number(event.target.value)); setPage(0); }}
                    labelRowsPerPage={tr("rowsPerPage")}
                    backIconButtonText={tr("previousPage")}
                    nextIconButtonText={tr("nextPage")}
                    SelectProps={{ inputProps: { "aria-label": tr("rowsPerPage") }, disabled }}
                    labelDisplayedRows={({ from, to, count }) => <span role="status">{tr("candidateRange", { from, to, total: count })}</span>}
                    backIconButtonProps={{ "aria-label": tr("previousPage"), disabled: disabled || currentPage === 0 }}
                    nextIconButtonProps={{ "aria-label": tr("nextPage"), disabled: disabled || (currentPage + 1) * pageSize >= visibleItems.length }}
                  />
                </div>
              ) : (
                <div className={styles.empty}>{tr("empty")}</div>
              )}


            </>
          )}
        </>
      )}

      <Dialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ className: styles.dialogPaper }}
        aria-labelledby="description-settings-title"
      >
        <div className={styles.dialogTitle}>
          <div className={styles.dialogTitleCopy}>
            <h2 id="description-settings-title">{tr("analysisSettings")}</h2>
            <p>{tr("analysisSettingsHelp")}</p>
          </div>
          <IconButton
            aria-label={t("common.close")}
            onClick={() => setSettingsOpen(false)}
          >
            <CloseIcon />
          </IconButton>
        </div>
        <DialogContent className={styles.dialogContent}>
          <div className={styles.settingsGrid}>
            {lifecycleEnabled && (
              <FormControlLabel
                className={styles.refreshPolicy}
                classes={{ label: styles.refreshPolicyLabel }}
                label={tr("refreshBeforeRebuild")}
                control={
                  <Checkbox
                    color="primary"
                    checked={policy.refresh_before_rebuild}
                    disabled={disabled}
                    inputProps={{ "aria-label": tr("refreshBeforeRebuild") }}
                    onChange={event =>
                      updatePolicy({
                        refresh_before_rebuild: event.target.checked
                      })
                    }
                  />
                }
              />
            )}
            <section className={styles.settingsSection}>
              <div className={styles.settingsSectionHeader}>
                <h3>{tr("analysisSourcesTitle")}</h3>
                <p>{tr("analysisSourcesHelp")}</p>
              </div>
              <div className={styles.sourceChoices}>
                {["description", "description_short"].map(field => (
                  <FormControlLabel
                    key={field}
                    label={
                      <span className={styles.sourceChoiceCopy}>
                        <strong>{tr(field)}</strong>
                        <small>{tr(`${field}Help`)}</small>
                      </span>
                    }
                    control={
                      <Checkbox
                        color="primary"
                        disabled={disabled}
                        checked={config.source_fields.includes(field)}
                        inputProps={{ "aria-label": tr(field) }}
                        onChange={event =>
                          setConfig({
                            ...config,
                            source_fields: event.target.checked
                              ? [...config.source_fields, field].sort()
                              : config.source_fields.filter(
                                  value => value !== field
                                )
                          })
                        }
                      />
                    }
                  />
                ))}
              </div>
            </section>
            <section className={styles.settingsSection}>
              <div className={styles.settingsSectionHeader}>
                <h3>{tr("analysisFormatTitle")}</h3>
              </div>
              <div
                className={styles.formatChoices}
                role="radiogroup"
                aria-label={tr("format")}
              >
                {["auto", "html", "text"].map(mode => (
                  <button
                    key={mode}
                    type="button"
                    role="radio"
                    aria-checked={config.mode === mode}
                    aria-label={tr(mode)}
                    disabled={disabled}
                    onClick={() => setConfig({ ...config, mode })}
                  >
                    <strong>{tr(mode)}</strong>
                    <span>{tr(`formatHelp.${mode}`)}</span>
                  </button>
                ))}
              </div>
            </section>
            <section className={styles.settingsSection}>
              <div className={styles.settingsSectionHeader}>
                <h3>{tr("analysisSeparatorTitle")}</h3>
              </div>
              <TextField
                id="description-separator"
                InputProps={{ classes: inputClasses }}
                className={styles.settingsField}
                label={tr("analysisNameValueSeparator")}
                value={config.separator}
                onChange={event =>
                  setConfig({ ...config, separator: event.target.value })
                }
                disabled={disabled}
                inputProps={{ maxLength: 32 }}
                variant="outlined"
                size="small"
                fullWidth
              />
              <p className={styles.separatorHelp}>
                {tr("analysisSeparatorHelp")}
              </p>
              <div className={styles.patternExample}>
                <span>{tr("analysisExample")}</span>
                <p>
                  {tr("analysisExampleValue", {
                    separator: config.separator
                  })}
                </p>
              </div>
            </section>
          </div>
        </DialogContent>
        <DialogActions className={styles.settingsActions}>
          <Button
            color="primary"
            variant="contained"
            onClick={() => setSettingsOpen(false)}
          >
            {tr("done")}
          </Button>
        </DialogActions>
      </Dialog>

      <DescriptionParameterEditor
        open={!!editorItem}
        item={editorItem}
        decision={editorDecision}
        groups={allGroups}
        independentTarget={editorDecision ? independentTarget(editorItem) : ""}
        disabled={sourceDisabled}
        normalizationEnabled={normalizationEnabled}
        valuePreviewApi={api}
        tr={tr}
        closeLabel={t("common.close")}
        cancelLabel={t("common.cancel")}
        createRoleDecision={(role, current) =>
          createRoleDecision(editorItem, role, current)
        }
        onClose={() => setEditorItem(null)}
        onCommit={({ decision, groupMetadata }) => {
          commitDecision(editorItem, decision, groupMetadata);
          setEditorItem(null);
        }}
      />

      <UnsavedChangesDialog guard={guard} />
      <DescriptionParametersConfirmDialog
        open={!!confirm}
        title={tr("discardTitle")}
        body={tr("discardHelp")}
        closeLabel={t("common.close")}
        discardLabel={tr("discard")}
        keepLabel={tr("keep")}
        onDiscard={() => {
          const action = confirm;
          setConfirm(null);
          if (action === "analyze") analyze();
          else reload();
        }}
        onKeep={() => setConfirm(null)}
      />
    </SettingsCard>
  );
  return renderPage ? renderPage({ actions: pageActions, children: content }) : content;
}
